import { User } from 'firebase/auth';

import {
  EDestinationSwitcher,
  EDurationSwitcher,
} from '@/src/components/cross-sale/useCrossSale';
import {
  IDestinationWithPromo,
  IZoneWithDestinationsAndPromo,
} from '@/src/modules/destinations/destinations.modules';
import { formatVolume } from '@/src/utils/format/volume';
import { formatDuration } from '@/src/utils/format/date';
import { applyDiscount } from '@/src/utils/format/amount';
import { IFirestoreOffer } from '@/src/modules/firestore/offers/offers.interface';
import { IFirestorePromo } from '@/src/modules/firestore/promos/promos.interface';

export const generateDataLayerViewItemList = (
  destinations: Record<string, IZoneWithDestinationsAndPromo>,
  destinationTypeSwitcher: EDestinationSwitcher,
  selectedDuration: EDurationSwitcher
) => {
  return {
    event: 'view_item_list',
    ecommerce: {
      currency: 'EUR',
      items: [
        Object.values(destinations).map((value, index) => {
          if (value.offers.length > 0) {
            const offer = value.offers.find(
              o => o.duration === selectedDuration
            );

            if (offer)
              return {
                item_id: offer.id,
                item_name: `eSIM for ${offer.id}`,
                item_variant: formatVolume(offer.volume),
                item_category: formatDuration(offer.duration),
                item_category2: offer.zone,
                quantity: 1,
                discount:
                  offer.promo && offer.promo.type === 'flatDiscount'
                    ? applyDiscount(offer, offer.promo).amount / 100
                    : 0,
                price: offer.basePriceEuros / 100,
                item_list_name: destinationTypeSwitcher,
              };
          }
        }),
      ],
    },
  };
};

export const generateDataLayerClickItemList = (
  offer: IDestinationWithPromo,
  destinationTypeSwitcher: EDestinationSwitcher
) => ({
  event: 'select_item',
  ecommerce: {
    currency: 'EUR',
    items: [
      {
        item_id: offer.id,
        item_name: `eSIM for ${offer.id}`,
        item_variant: formatVolume(offer.volume),
        item_category: formatDuration(offer.duration),
        item_category2: offer.zone,
        quantity: 1,
        discount:
          offer.promo && offer.promo.type === 'flatDiscount'
            ? applyDiscount(offer, offer.promo).amount / 100
            : 0,
        price: offer.basePriceEuros / 100,
        item_list_name: destinationTypeSwitcher,
      },
    ],
  },
});

export const generateDataLayerBeginCheckout = (
  user: User,
  offer: IFirestoreOffer,
  promo?: IFirestorePromo
) => {
  const discounted = applyDiscount(offer, promo);

  return {
    event: 'begin_checkout',
    user_email: user.email,
    user_id: user.uid,
    ecommerce: {
      currency: 'EUR',
      value: discounted.amount / 100,
      items: [
        {
          item_id: offer.id,
          item_name: `eSIM for ${offer.id}`,
          item_variant: formatVolume(discounted.volume),
          item_category: formatDuration(offer.duration),
          item_category2: offer.id,
          quantity: 1,
          discount:
            promo && promo.type === 'flatDiscount'
              ? applyDiscount(offer, promo).amount / 100
              : 0,
          price: offer.basePriceEuros / 100,
        },
      ],
    },
  };
};

export const generateDataLayerLogin = (user: User) => ({
  event: 'login',
  user_id: user.uid,
  user_email: user.email,
});

export const generateDataLayerFAQSelect = (faqId: string) => ({
  event: 'click_faq',
  faq: faqId,
});

export const generateDataLayerContact = (email?: string) => ({
  event: 'click_email',
  email_contact: email ?? 'support@example.com',
});
